﻿import { Box, Button, Checkbox, FormControl, Grid, HStack, Stack, Text } from '@chakra-ui/react'
import type { PersonAdditionalData, PolicyHolderAdditionalData, SmartTravelInsuredPerson } from 'BrokerTsLib/src/dtos'
import { createSmartTravelPersonFromK1, defaultSmartTravelInsuredPerson, getSeparateInsuredPerson } from 'BrokerTsLib/src/helpers/smartTravel'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useContext } from 'react'
import { ConfirmRemoveButton } from '../../components/ConfirmRemoveButton'
import { Datepicker } from '../../components/Datepicker'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import { InputGroup } from '../../components/InputGroup'
import { Switch } from '../../components/Switch'
import { SwitchControl } from '../../components/SwitchControl'
import { Add } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { CountrySelect } from '../CountrySelect'

const name = 'property.additionalDataGcp.smartTravel.insuredPersons'

const defaultPerson = defaultSmartTravelInsuredPerson

export const SmartTravelInsuredPersons = () => {
  const { control, watch, getValues, getFieldState, formState } = useFormContext()
  const { insuranceParams } = useContext(insuranceParamsContext)
  const contracted = !!insuranceParams?.generalParams.contractNumber

  const { fields, append, update, remove } = useFieldArray({
    control,
    name
  })

  const persons = fields as unknown as (SmartTravelInsuredPerson & { id: string })[]

  const insuredCount = watch('property.additionalDataGcp.smartTravel.insuredCount') as number
  const isFamily = insuredCount == 3
  const maxPersons = isFamily ? 7 : (insuredCount ?? 1)
  const minPersons = isFamily ? 3 : (insuredCount ?? 1)

  const policyHolderPerson = getValues('policyHolder.person') as PersonAdditionalData | null
  const insuredPerson = getSeparateInsuredPerson(getValues('policyHolder') as PolicyHolderAdditionalData | null)
  const hasSeparateInsuredPerson = !!insuredPerson

  // pokud je pojištěný shodný s pojistníkem, nese osoba příznak isPolicyHolder (builder GČP s ní pak správně pracuje)
  const insuredChecked = persons.some(p => hasSeparateInsuredPerson ? p.isInsuredPerson : p.isPolicyHolder)
  const policyHolderChecked = persons.some(p => p.isPolicyHolder)

  // checkbox nemění počet řádků - zaškrtnutí předvyplní volný řádek údaji osoby z K1, odškrtnutí hodnoty smaže
  const handleToggle = (checked: boolean, source: PersonAdditionalData | null, flags: { isPolicyHolder: boolean, isInsuredPerson: boolean }) => {
    if (checked) {
      const freeIndex = persons.findIndex(p => !p.isPolicyHolder && !p.isInsuredPerson)
      const person = createSmartTravelPersonFromK1(source, flags)

      if (freeIndex >= 0) update(freeIndex, person)
      else if (persons.length < maxPersons) append(person)
    } else {
      const index = persons.findIndex(p => flags.isInsuredPerson ? p.isInsuredPerson : p.isPolicyHolder)
      if (index >= 0) update(index, { ...defaultPerson })
    }
  }

  const handleToggleInsured = (checked: boolean) =>
    handleToggle(checked,
      hasSeparateInsuredPerson ? insuredPerson : policyHolderPerson,
      { isPolicyHolder: !hasSeparateInsuredPerson, isInsuredPerson: hasSeparateInsuredPerson })

  const handleTogglePolicyHolder = (checked: boolean) =>
    handleToggle(checked, policyHolderPerson, { isPolicyHolder: true, isInsuredPerson: false })

  const { error } = getFieldState(name, formState)

  return (
    <Box sx={{ display: 'grid', gap: 4, justifyItems: 'stretch', w: 'full' }}>
      <Text fontSize='sm' color='gray.600'>
        {isFamily
          ? 'Chytré cestovko je sjednáno pro rodinu – zadejte 3 až 7 pojištěných osob, z toho maximálně 2 osoby starší 26 let.'
          : `Chytré cestovko je sjednáno pro ${maxPersons == 1 ? '1 pojištěnou osobu' : '2 pojištěné osoby'}.`
        }
      </Text>

      <Stack gap={2}>
        <Checkbox
          isChecked={insuredChecked}
          isDisabled={contracted}
          onChange={(e) => handleToggleInsured(e.target.checked)}
        >
          <Text as='span' fontWeight='normal'>
            Je <Text as='span' fontWeight='bold'>pojištěný</Text> zároveň pojištěnou osobou chytrého cestovka
          </Text>
        </Checkbox>

        {hasSeparateInsuredPerson &&
          <Checkbox
            isChecked={policyHolderChecked}
            isDisabled={contracted}
            onChange={(e) => handleTogglePolicyHolder(e.target.checked)}
          >
            <Text as='span' fontWeight='normal'>
              Je <Text as='span' fontWeight='bold'>pojistník</Text> zároveň pojištěnou osobou chytrého cestovka
            </Text>
          </Checkbox>
        }
      </Stack>

      {persons.map((person, index) => {
        const flagged = !!person.isPolicyHolder || !!person.isInsuredPerson

        return (
          <SmartTravelInsuredPersonRow
            key={person.id}
            index={index}
            readonly={contracted || flagged}
            canRemove={!contracted && !flagged && persons.length > minPersons}
            remove={remove}
          />
        )
      })}

      {error?.message &&
        <Text fontSize='sm' color='red.500'>{error.message}</Text>
      }

      {!contracted && isFamily && persons.length < maxPersons &&
        <Button type='button' variant='outline' onClick={() => append({ ...defaultPerson })}>
          <Add color='secondary.500' />
          Přidat další osobu
        </Button>
      }
    </Box>
  )
}

type SmartTravelInsuredPersonRowProps = {
  index: number
  readonly: boolean
  canRemove: boolean
  remove: UseFieldArrayRemove
}

const SmartTravelInsuredPersonRow = ({
  index,
  readonly,
  canRemove,
  remove
}: SmartTravelInsuredPersonRowProps) => {
  const { register, setValue, watch, getValues, getFieldState, formState } = useFormContext()

  const prefix = `${name}.${index}`

  const isForeigner = watch(`${prefix}.isForeigner`)

  const firstNameError = getFieldState(`${prefix}.firstName`, formState).error
  const lastNameError = getFieldState(`${prefix}.lastName`, formState).error
  const birthnumberError = getFieldState(`${prefix}.birthnumber`, formState).error
  const birthdateError = getFieldState(`${prefix}.birthdate`, formState).error

  return (
    <FormGroup
      sx={{
        display: 'grid',
        // pevná šablona + placeholder místo tlačítka Odebrat, aby měly všechny řádky stejně široké sloupce
        gridTemplateColumns: { base: '1fr', md: '1fr 1fr 1.6fr 130px' },
        columnGap: 4,
        rowGap: 4,
        p: 4,
        justifyItems: 'stretch',
        alignItems: 'start',
        bg: 'gray.100',
        borderRadius: 'md',
        w: 'full',
      }}>
      <FormControl isRequired={!readonly} isInvalid={!!firstNameError}>
        <FormLabel>Jméno</FormLabel>

        <InputGroup
          htmlSize={12}
          placeholder="Jan"
          isDisabled={readonly}
          errors={firstNameError}
          {...register(`${prefix}.firstName`)}
        />
      </FormControl>

      <FormControl isRequired={!readonly} isInvalid={!!lastNameError}>
        <FormLabel>Příjmení</FormLabel>

        <InputGroup
          htmlSize={12}
          placeholder="Novák"
          isDisabled={readonly}
          errors={lastNameError}
          {...register(`${prefix}.lastName`)}
        />
      </FormControl>

      <FormControl isInvalid={!!birthnumberError}>
        <FormLabel
          tooltip='U osob, které nemají přiděleno rodné číslo (cizí státní příslušníci), zašktněte volbu "Nemá" a vyberte státní občanství.'
        >
          Rodné číslo
        </FormLabel>

        <HStack>
          <SwitchControl>
            <Switch
              {...register(`${prefix}.isForeigner`, {
                onChange: (e) => {
                  if (e.target.checked) {
                    setValue(`${prefix}.birthnumber`, '')
                  } else {
                    setValue(`${prefix}.birthdate`, null)
                    setValue(`${prefix}.nationalityFK`, null)
                  }
                }
              })}
              isDisabled={readonly}
            />
            <FormLabel htmlFor={`${prefix}.isForeigner`} variant="switch">
              Nemá
            </FormLabel>
          </SwitchControl>

          {!isForeigner &&
            <InputGroup
              htmlSize={12}
              isDisabled={readonly}
              errors={birthnumberError}
              {...register(`${prefix}.birthnumber`, {
                setValueAs: (v: string) => (v ?? '').replace(/\D/g, '')
              })}
            />
          }

          {isForeigner &&
            <CountrySelect
              {...register(`${prefix}.nationalityFK`)}
              isDisabled={readonly}
            />
          }
        </HStack>
      </FormControl>

      {/* delší potvrzovací text se zalomí uvnitř tlačítka, aby nepřetékal mimo sloupec */}
      <Grid sx={{ pt: 6, justifyItems: 'start', '& button': { whiteSpace: 'normal', h: 'auto', minH: 10, py: 2 } }}>
        {canRemove &&
          <ConfirmRemoveButton
            hideCancelButton
            fullWidth={false}
            onClick={() => remove(index)}
          />
        }
      </Grid>

      {isForeigner &&
        <FormControl isRequired={!readonly} isInvalid={!!birthdateError}>
          <FormLabel>Datum narození</FormLabel>

          <Datepicker
            isDisabled={readonly}
            errors={birthdateError}
            date={getValues(`${prefix}.birthdate`)}
            onDateChange={(date) => setValue(`${prefix}.birthdate`, date)}
          />
        </FormControl>
      }
    </FormGroup>
  )
}
